import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react";
import { AppLayout } from "../shared/layout";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  ExternalLink,
  Filter,
  Users,
} from "lucide-react";
import {
  getTasks,
  updateTaskStatus,
} from "../../../data/requests/task-requests";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { toast } from "@/components/ui/use-toast";

const TaskList = observer(() => {
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const queryClient = useQueryClient();

  // URL parametresinden filter değerini oku
  useEffect(() => {
    const urlFilter = searchParams.get("filter");
    if (urlFilter && ["all", "created", "assigned"].includes(urlFilter)) {
      setFilter(urlFilter);
    }
  }, [searchParams]);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["tasks", filter],
    queryFn: () =>
      getTasks(filter === "all" ? null : filter).then((res) => res.data),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ taskId, status }) => updateTaskStatus(taskId, status),
    onSuccess: () => {
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["my-task-count"]);
      toast({
        title: "Başarılı",
        description: "Task durumu güncellendi",
      });
    },
    onError: () => {
      toast({
        title: "Hata",
        description: "Task durumu güncellenirken hata oluştu",
        variant: "destructive",
      });
    },
  });

  const getPriorityBadge = (priority) => {
    const variants = {
      Acil: { variant: "destructive", icon: AlertCircle },
      Normal: { variant: "default", icon: Clock },
      Düşük: { variant: "secondary", icon: CheckCircle2 },
    };
    const config = variants[priority] || variants["Normal"];
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className="h-3 w-3" />
        {priority}
      </Badge>
    );
  };

  const handleStatusChange = (taskId, newStatus) => {
    updateStatusMutation.mutate({ taskId, status: newStatus });
  };

  // Atanan kullanıcıları render et
  const renderAssignedUsers = (task) => {
    const users = task.assignedUsers || [];
    
    if (users.length === 0) {
      return <span className="text-muted-foreground">-</span>;
    }

    if (users.length === 1) {
      return <span>{users[0].name}</span>;
    }

    // Birden fazla kullanıcı varsa title ile hover'da göster
    const userNames = users.map((u) => u.name).join(", ");
    return (
      <div 
        className="flex items-center gap-1 cursor-default"
        title={userNames}
      >
        <Users className="h-4 w-4" />
        <span>{users.length} kişi</span>
      </div>
    );
  };

  const filteredTasks =
    statusFilter === "all"
      ? tasks
      : tasks.filter((task) => task.status === statusFilter);

  return (
    <AppLayout title="Görevler">
      <div className="container mx-auto py-6 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Görev Listesi</CardTitle>
                <CardDescription>
                  Tüm görevlerinizi buradan görüntüleyebilirsiniz
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Görevler</SelectItem>
                    <SelectItem value="created">Oluşturduklarım</SelectItem>
                    <SelectItem value="assigned">Atananlar</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Durumlar</SelectItem>
                    <SelectItem value="Açık">Açık</SelectItem>
                    <SelectItem value="Devam Ediyor">Devam Ediyor</SelectItem>
                    <SelectItem value="Tamamlandı">Tamamlandı</SelectItem>
                    <SelectItem value="İptal">İptal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Yükleniyor...</div>
            ) : filteredTasks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Henüz görev bulunmuyor
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Başlık</TableHead>
                    <TableHead>Öncelik</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>Oluşturan</TableHead>
                    <TableHead>Atanan</TableHead>
                    <TableHead>Oluşturulma</TableHead>
                    <TableHead>İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell className="font-medium">
                        <Link
                          to={`/app/tasks/${task.id}`}
                          className="hover:underline flex items-center gap-2"
                        >
                          {task.title}
                          <ExternalLink className="h-3 w-3" />
                        </Link>
                      </TableCell>
                      <TableCell>{getPriorityBadge(task.priority)}</TableCell>
                      <TableCell>
                        <Select
                          value={task.status}
                          onValueChange={(value) =>
                            handleStatusChange(task.id, value)
                          }
                        >
                          <SelectTrigger className="w-[150px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Açık">Açık</SelectItem>
                            <SelectItem value="Devam Ediyor">
                              Devam Ediyor
                            </SelectItem>
                            <SelectItem value="Tamamlandı">
                              Tamamlandı
                            </SelectItem>
                            <SelectItem value="İptal">İptal</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>{task.createdBy.name}</TableCell>
                      <TableCell>{renderAssignedUsers(task)}</TableCell>
                      <TableCell>
                        {format(new Date(task.createdAt), "dd MMM yyyy HH:mm", {
                          locale: tr,
                        })}
                      </TableCell>
                      <TableCell>
                        <Link to={`/app/tasks/${task.id}`}>
                          <Button variant="outline" size="sm">
                            Detay
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
});

export default TaskList;
